import { useState, useRef, useCallback } from 'react';
import { supabase } from '../../../lib/supabase';

interface ResizeOptions {
  maxWidth: number;
  maxHeight: number;
  quality: number;
}

interface UseImageUploadOptions {
  onUploaded: (publicUrl: string) => void;
  maxSizeMB?: number;
  resize?: ResizeOptions;
  pathPrefix?: string;
}

function resizeImage(file: File, opts: ResizeOptions): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const img = new window.Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      let { width, height } = img;
      if (width <= opts.maxWidth && height <= opts.maxHeight) {
        resolve(file);
        return;
      }
      const ratio = Math.min(opts.maxWidth / width, opts.maxHeight / height);
      width = Math.round(width * ratio);
      height = Math.round(height * ratio);
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d')!;
      ctx.drawImage(img, 0, 0, width, height);
      canvas.toBlob(
        (blob) => (blob ? resolve(blob) : reject(new Error('Resize failed'))),
        'image/jpeg',
        opts.quality
      );
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Failed to load image'));
    };
    img.src = url;
  });
}

export function useImageUpload({ onUploaded, maxSizeMB = 5, resize, pathPrefix = 'course-images' }: UseImageUploadOptions) {
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dragCounter = useRef(0);

  const upload = useCallback(async (file: File) => {
    if (!file.type.startsWith('image/')) {
      setError('Please select an image file');
      return;
    }
    if (file.size > maxSizeMB * 1024 * 1024) {
      setError(`Image must be smaller than ${maxSizeMB}MB`);
      return;
    }

    setIsUploading(true);
    setError(null);

    try {
      let uploadData: Blob = file;
      let ext = file.name.split('.').pop();

      if (resize) {
        uploadData = await resizeImage(file, resize);
        ext = 'jpg';
      }

      const filename = `${crypto.randomUUID()}.${ext}`;
      const path = `${pathPrefix}/${filename}`;

      const { error: uploadError } = await supabase.storage
        .from('course-assets')
        .upload(path, uploadData);

      if (uploadError) throw uploadError;

      const { data: urlData } = supabase.storage
        .from('course-assets')
        .getPublicUrl(path);

      onUploaded(urlData.publicUrl);
    } catch (err: any) {
      console.error('Upload failed:', err);
      setError(err.message || 'Failed to upload image');
    } finally {
      setIsUploading(false);
    }
  }, [onUploaded, maxSizeMB, resize]);

  const onDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const onDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current++;
    if (e.dataTransfer.types.includes('Files')) {
      setIsDragging(true);
    }
  }, []);

  const onDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current--;
    if (dragCounter.current === 0) {
      setIsDragging(false);
    }
  }, []);

  const onDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current = 0;
    setIsDragging(false);

    const file = e.dataTransfer.files?.[0];
    if (file) {
      upload(file);
    }
  }, [upload]);

  const openFilePicker = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const onFileInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      upload(file);
    }
    // Reset so the same file can be selected again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [upload]);

  const clearError = useCallback(() => setError(null), []);

  return {
    isUploading,
    error,
    isDragging,
    upload,
    clearError,
    openFilePicker,
    fileInputRef,
    onFileInputChange,
    dragHandlers: { onDragOver, onDragEnter, onDragLeave, onDrop },
  };
}
